// Congés / Récupération — shared vocabulary of the two leave pages.
// Helpers + tiny components ported from the design prototype's
// cg-data.jsx (date math, formatting, avatars) minus its demo dataset:
// all real data comes from /api/leaves via the hooks.
import { useEffect, useMemo, useRef, useState } from 'react';
import { ENTITIES, type EntityId } from '@pointage/shared';
import { useHolidays, type LeaveBalanceRow } from '../../api/hooks.js';

export const CG_MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export const CG_DOW = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

export const cgNum = (n: number) => (Math.round(n * 10) / 10).toFixed(1).replace('.', ',');
export const cgSigned = (n: number) => (n > 0 ? '+' : n < 0 ? '−' : '') + cgNum(Math.abs(n));
export const cgInitials = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');

// Deterministic avatar color per employee (same trick as the proto).
const AV_COLORS = ['#7037ef', '#0bb5ab', '#ff5e11', '#d24df3', '#2a6fdb', '#c41d8f', '#0a9d72', '#d8590f'];
export const cgAvColor = (id: string) =>
  AV_COLORS[[...id].reduce((a, c) => a + c.charCodeAt(0), 0) % AV_COLORS.length]!;

export const entityById = (id: string) => ENTITIES.find((e) => e.id === (id as EntityId));

// ── date helpers (YYYY-MM-DD strings, UTC-safe) ──────────────
export const cgD = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!));
};
export const cgIso = (dt: Date) =>
  `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
export const cgAddDays = (s: string, n: number) => {
  const d = cgD(s);
  d.setUTCDate(d.getUTCDate() + n);
  return cgIso(d);
};
export const cgToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// Algerian weekend (ven + sam) — mirrors the server's default workDays
// setting and the previous Balances page.
export const cgWeekend = (s: string) => {
  const w = cgD(s).getUTCDay();
  return w === 5 || w === 6;
};
export const cgRange = (from: string, to: string) => {
  const out: string[] = [];
  let c = from;
  let guard = 0;
  while (c <= to && guard++ < 400) { out.push(c); c = cgAddDays(c, 1); }
  return out;
};
export const cgFmtDate = (s: string) => {
  const d = cgD(s);
  return `${d.getUTCDate()} ${CG_MONTHS[d.getUTCMonth()]!.slice(0, 4)}.`;
};
export const cgFmtLong = (s: string) => {
  const d = cgD(s);
  return `${d.getUTCDate()} ${CG_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};
export const cgFmtRange = (f: string, t: string) =>
  f === t ? cgFmtLong(f)
    : cgD(f).getUTCMonth() === cgD(t).getUTCMonth() && cgD(f).getUTCFullYear() === cgD(t).getUTCFullYear()
      ? `${cgD(f).getUTCDate()} – ${cgFmtLong(t)}`
      : `${cgFmtDate(f)} – ${cgFmtLong(t)}`;
export const shiftYm = (ym: string, delta: number) => {
  const [y, m] = ym.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1 + delta, 1));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
};
export const ymLabel = (ym: string) => `${CG_MONTHS[Number(ym.split('-')[1]) - 1]} ${ym.split('-')[0]}`;

// Holidays for a [ym0 .. ym1] month window, as a date→name map.
export function useHolidayMap(ym0: string, ym1: string) {
  const from = `${ym0}-01`;
  const [y, m] = ym1.split('-').map(Number);
  const to = `${ym1}-${String(new Date(Date.UTC(y!, m!, 0)).getUTCDate()).padStart(2, '0')}`;
  const holidays = useHolidays(from, to);
  return useMemo(() => {
    const map = new Map<string, string>();
    for (const h of holidays.data ?? []) map.set(h.date, h.name);
    return map;
  }, [holidays.data]);
}

export const countWorkdays = (from: string, to: string, holidays: Map<string, string>) =>
  cgRange(from, to).filter((d) => !cgWeekend(d) && !holidays.has(d)).length;

// ── balances helpers ─────────────────────────────────────────
export const balanceByEmp = (rows: LeaveBalanceRow[] | undefined) => {
  const map = new Map<string, LeaveBalanceRow>();
  for (const r of rows ?? []) map.set(r.employee.id, r);
  return map;
};

// Filter a team by the top search box (name / role / code).
export const matchesQuery = (e: { name: string; role: string | null; code: string }, q: string) =>
  !q || `${e.name} ${e.role ?? ''} ${e.code}`.toLowerCase().includes(q.toLowerCase());

// ── animated KPI number (proto CgCount) ──────────────────────
export function CgCount({ value, dec = 1, suffix }: { value: number; dec?: number; suffix?: string }) {
  const [v, setV] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const from = prev.current;
    const to = value;
    const t0 = performance.now();
    const dur = 750;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setV(from + (to - from) * e);
      if (p < 1) raf = requestAnimationFrame(tick);
      else prev.current = to;
    };
    raf = requestAnimationFrame(tick);
    const safety = window.setTimeout(() => { setV(to); prev.current = to; cancelAnimationFrame(raf); }, dur + 150);
    return () => { cancelAnimationFrame(raf); clearTimeout(safety); };
  }, [value]);
  return <>{dec ? v.toFixed(dec).replace('.', ',') : Math.round(v)}{suffix && <i>{suffix}</i>}</>;
}

// ── mini month picker (proto CgMiniMonth) ────────────────────
// Monday-first grid. `onlyOff` flips it into the récup-credit mode
// where only weekends & holidays are selectable.
export function MiniMonth({ month, sel, hoverEnd, onPick, onHover, onlyOff, holidays }: {
  month: string;
  sel: { from: string | null; to: string | null };
  hoverEnd?: string | null;
  onPick: (d: string) => void;
  onHover?: (d: string) => void;
  onlyOff?: boolean;
  holidays: Map<string, string>;
}) {
  const [y, m] = month.split('-').map(Number);
  const nDays = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  const first = new Date(Date.UTC(y!, m! - 1, 1)).getUTCDay();
  const lead = (first + 6) % 7;
  const cells: Array<string | null> = [
    ...Array(lead).fill(null) as null[],
    ...Array.from({ length: nDays }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`),
  ];
  const today = cgToday();
  const end = sel.to ?? hoverEnd ?? null;
  const inRange = (d: string) =>
    !!sel.from && !!end && d >= (sel.from < end ? sel.from : end) && d <= (sel.from < end ? end : sel.from);
  return (
    <div className="cg-mm">
      <div className="cg-mm-h">{ymLabel(month)}</div>
      <div className="cg-mm-g">
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <span key={i} className="cg-mm-w">{d}</span>)}
        {cells.map((d, i) => {
          if (!d) return <span key={i} />;
          const hol = holidays.get(d);
          const off = cgWeekend(d) || !!hol;
          if (onlyOff) {
            return (
              <button key={d} type="button" title={hol ?? (off ? 'Week-end' : '')} disabled={!off}
                className={'cg-mm-d offpick' + (off ? ' can' : ' dis') + (hol ? ' hol' : '') + (d === sel.from ? ' a b' : '') + (d === today ? ' today' : '')}
                onClick={() => onPick(d)}>
                {cgD(d).getUTCDate()}
              </button>
            );
          }
          return (
            <button key={d} type="button" title={hol ?? ''}
              className={'cg-mm-d' + (off ? ' off' : '') + (inRange(d) ? ' in' : '') + (d === sel.from ? ' a' : '') + (d === sel.to ? ' b' : '') + (d === today ? ' today' : '')}
              onClick={() => onPick(d)} onMouseEnter={() => onHover?.(d)}>
              {cgD(d).getUTCDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Compact employee cell used by the tables.
export function EmpCell({ emp }: { emp: { id: string; name: string; role: string | null; entityId: string; code: string } }) {
  const ent = entityById(emp.entityId);
  return (
    <div className="gm-emp">
      <span className="gm-av" style={{ background: cgAvColor(emp.id) }}>{cgInitials(emp.name)}</span>
      <span className="gm-empmeta">
        <span className="gm-name">{emp.name}</span>
        <span className="gm-role">
          {ent && <i className="cg-entdot" style={{ background: ent.color }} />}
          {emp.role ?? emp.code}{ent ? ` · ${ent.short}` : ''}
        </span>
      </span>
    </div>
  );
}
